import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AgentLog, IRaiseMismatch } from '../api/queries';
import { formatTime, formatTimeHandler } from '../utils';
import { BtnDefault, BtnPrimary } from './common/Buttons';
import { useThemeStore } from '../state/theme';
import {
  MisMatchRequestInputs,
  mismatchRequestSchema,
} from '../validations/mismatch';
import {
  approveMismatchRequest,
  updateMismatchRequestStatus,
} from '../hooks/mutations';
import moment from 'moment';
import { IoCopy } from 'react-icons/io5';
import toast from 'react-hot-toast';

const MismatchRequest: React.FC<{
  mismatchRequest?: IRaiseMismatch;
  agentLogForMismatch?: AgentLog;
  onEdit: () => void;
  onEditError: (error: any) => void;
  onStatusUpdate: (status?: string) => void;
  onStatusUpdateError: (error: any) => void;
}> = ({
  mismatchRequest,
  agentLogForMismatch,
  onEdit,
  onEditError,
  onStatusUpdate,
  onStatusUpdateError,
}) => {
  if (!mismatchRequest || !agentLogForMismatch) return null;

  const { isDarkMode } = useThemeStore();

  const { mutate: approveMismatchRequestMutate, isPending } =
    approveMismatchRequest({
      onError: onEditError,
      onSuccess: onEdit,
    });

  const {
    mutate: updateMismatchRequestStatusMutate,
    isPending: isUpdatingMismatchRequestStatus,
  } = updateMismatchRequestStatus({
    onError: onStatusUpdateError,
    onSuccess: (data) => onStatusUpdate(data.data?.status),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<MisMatchRequestInputs>({
    resolver: zodResolver(mismatchRequestSchema),
    defaultValues: {
      startTime: mismatchRequest.startTime,
      stopTime: mismatchRequest.stopTime,
    },
  });

  const duration =
    watch('startTime') && watch('stopTime')
      ? Math.abs(
          new Date(watch('stopTime')).getTime() -
            new Date(watch('startTime')).getTime()
        ) / 1000
      : 0;

  const onSubmit = async (data: MisMatchRequestInputs) =>
    approveMismatchRequestMutate({
      ...data,
      _id: mismatchRequest._id,
      logId: agentLogForMismatch._id,
      duration: duration / 60,
      timeZone: agentLogForMismatch.timeZone || moment.tz.guess(),
    });

  const handleReject = () => {
    updateMismatchRequestStatusMutate({
      _id: mismatchRequest._id,
      status: 'rejected',
    });
  };

  const inputClassName = `w-full rounded-md px-3 py-2 shadow-sm focus:outline-none ${
    isDarkMode
      ? 'bg-gray-700 border-gray-600 text-gray-100 focus:border-indigo-500 focus:ring-indigo-500'
      : 'border border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
  }`;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={`overflow-y-auto h-[calc(100vh-5rem)] ${
        isDarkMode ? 'text-white' : 'text-black'
      }`}
    >
      <div className="mb-4 space-y-2">
        <label className="block text-sm mb-1">Request ID:</label>
        <div className="flex items-center gap-2">
          <b>{mismatchRequest._id}</b>
          <span className="cursor-pointer">
            <IoCopy
              onClick={() => {
                navigator.clipboard.writeText(mismatchRequest._id);
                toast.success('Request ID copied');
              }}
              width={16}
              height={16}
            />
          </span>
        </div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm mb-1">Date of mismatch:</label>
          <b>
            {new Date(mismatchRequest?.startTime).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: '2-digit',
            })}
          </b>
        </div>
        <div className="space-y-2">
          <label className="block text-sm mb-1">Status:</label>
          <span
            className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
              mismatchRequest.status === 'pending'
                ? 'bg-yellow-100 text-yellow-800'
                : mismatchRequest.status === 'resolved'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
            }`}
          >
            {mismatchRequest.status.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="block text-sm mb-1">Kim ID:</label>
          <b>{mismatchRequest.kimId}</b>
        </div>
        <div className="space-y-2">
          <label className="block text-sm mb-1">Account:</label>
          <b>{mismatchRequest.account}</b>
        </div>
      </div>

      {(mismatchRequest.shiftId || agentLogForMismatch.shiftId) && (
        <div className="mb-4 space-y-2">
          <label className="block text-sm mb-1">Shift ID:</label>
          <b>{mismatchRequest.shiftId || agentLogForMismatch.shiftId}</b>
        </div>
      )}

      <div className="mb-4 space-y-2">
        <label className="block text-sm mb-1">System recorded log:</label>
        <div className="p-4 rounded-md border space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500">
                Total hours worked
              </label>
              <div className="font-medium">
                {formatTime(Math.floor(agentLogForMismatch?.duration) * 60)}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-500">Idle time</label>
              <div className="font-medium">
                {formatTime((agentLogForMismatch?.idleTime || 0) * 60)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-500">Start time</label>
              <div className="font-medium">
                {formatTimeHandler(agentLogForMismatch?.startTime).format(
                  'MMM DD, YYYY hh:mm A'
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-500">End time</label>
              <div className="font-medium">
                {formatTimeHandler(agentLogForMismatch?.stopTime).format(
                  'MMM DD, YYYY hh:mm A'
                )}
              </div>
            </div>
          </div>

          {agentLogForMismatch?.breaks &&
            agentLogForMismatch.breaks.length > 0 && (
              <div>
                <label className="block text-sm text-gray-500 mb-2">
                  Breaks
                </label>
                <div className="space-y-2">
                  {agentLogForMismatch.breaks.map((breakItem, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded text-sm ${
                        isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between">
                        <span className="font-medium">{breakItem.type}</span>
                        <span>{formatTime(breakItem.duration * 60)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {formatTimeHandler(breakItem.startedAt).format(
                          'hh:mm A'
                        )}{' '}
                        -{' '}
                        {formatTimeHandler(breakItem.endedAt).format('hh:mm A')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {agentLogForMismatch?.deviations &&
            agentLogForMismatch.deviations.length > 0 && (
              <div>
                <label className="block text-sm text-gray-500 mb-2">
                  Deviations
                </label>
                <div className="space-y-2">
                  {agentLogForMismatch.deviations.map((deviation, idx) => (
                    <div
                      key={idx}
                      className={`p-2 rounded text-sm ${
                        isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                      }`}
                    >
                      <div className="font-medium">
                        {deviation.type.replace(/_/g, ' ')}
                      </div>
                      <div className="text-xs mt-1">
                        <span className="text-gray-500">Scheduled: </span>
                        {formatTimeHandler(deviation.scheduledTime).format(
                          'hh:mm A'
                        )}
                        <span className="text-gray-500 ml-3">Actual: </span>
                        {formatTimeHandler(deviation.actualTime).format(
                          'hh:mm A'
                        )}
                      </div>
                      {deviation.reason && (
                        <div className="text-xs text-gray-500 mt-1">
                          {deviation.reason}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      <div className="mb-4 space-y-2">
        <label className="block text-sm mb-1">Requested changes:</label>
        <div className="flex flex-col p-4 rounded-md border">
          {(errors.startTime || errors.stopTime) && (
            <p className="text-red-500 mb-4">
              {errors.startTime?.message} {errors.stopTime?.message}
            </p>
          )}

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="space-y-1">
              <label className="block text-sm">Log start time</label>
              <Controller
                name="startTime"
                control={control}
                render={({ field }) => (
                  <input
                    type="datetime-local"
                    className={inputClassName}
                    value={moment(field.value).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) =>
                      field.onChange(new Date(e.target.value).toISOString())
                    }
                    onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                  />
                )}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm">Log end time</label>
              <Controller
                name="stopTime"
                control={control}
                render={({ field }) => (
                  <input
                    type="datetime-local"
                    className={inputClassName}
                    value={moment(field.value).format('YYYY-MM-DDTHH:mm')}
                    onChange={(e) =>
                      field.onChange(new Date(e.target.value).toISOString())
                    }
                    onClick={(e) => (e.target as HTMLInputElement).showPicker()}
                  />
                )}
              />
            </div>
            <div className="space-y-1">
              <label className="block text-sm">Total hours worked</label>
              <div className="font-medium">{formatTime(duration)}</div>
            </div>
          </div>

          {mismatchRequest.comment && (
            <div className="mb-4 space-y-1">
              <label className="block text-sm">Additional comments</label>
              <div className="text-sm p-2 rounded-md border">
                {mismatchRequest.comment}
              </div>
            </div>
          )}

          {mismatchRequest.files.length > 0 && (
            <div className="mb-6 space-y-1">
              <label className="block text-sm">Screenshots</label>
              <div className="flex flex-col gap-2">
                {mismatchRequest.files.map((file, idx) => (
                  <a
                    key={idx}
                    href={file}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm underline"
                  >
                    {file}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-between mb-8">
        {mismatchRequest.status === 'pending' && (
          <BtnDefault
            className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
            type="button"
            onClick={handleReject}
            disabled={isPending || isUpdatingMismatchRequestStatus}
          >
            {isUpdatingMismatchRequestStatus
              ? 'Rejecting...'
              : 'Reject Request'}
          </BtnDefault>
        )}
        <BtnPrimary
          type="submit"
          disabled={isPending || isUpdatingMismatchRequestStatus}
        >
          {isPending ? 'Editing...' : 'Edit Work Log'}
        </BtnPrimary>
      </div>
    </form>
  );
};

export default MismatchRequest;
